import { AxiosError } from 'axios'
import { hasConversationResponseTimer } from 'conversations/modules/gates/selectors'
import { abilityEnabledFromAnyTeam } from 'conversations/modules/teams/selectors'
import { isEndUser } from 'conversations/utils/endUserUtils'
import {
  agentMessageCondition,
  botMessageCondition,
  endUserMessageCondition,
  joinEventCondition,
} from 'conversations/utils/Message'
import { isDefined } from 'conversations/utils/typescriptHelpers'
import { createSelector } from 'reselect'
import { FETCH_MESSAGES_FAIL, FETCH_MESSAGES_REQUEST } from 'shared/store/actions/message/constants'
import { Attachment } from 'shared/typings/conversations/Attachment'
import { ConversationEventType } from 'shared/typings/messages/conversationevents/ConversationEventType'
import { Message } from 'shared/typings/messages/Message'
import { MessageType } from 'shared/typings/messages/MessageType'
import { UserIdType } from 'shared/typings/UserIdType'

import { RootState } from 'typesafe-actions'

const root = (state: RootState) => state.convo.message
const emptyArray: any[] = []

export const getHasMoreMessages = (conversationId?: number) => (state: RootState) => {
  const pagination = conversationId && root(state).paginationByConversationId[conversationId]
  return pagination ? pagination.more : false
}

export const getLoadingMoreMessages =
  (conversationId?: number) =>
  (state: RootState): boolean =>
    (conversationId &&
      root(state).statusByConversationId[conversationId] ===
        `${FETCH_MESSAGES_REQUEST}_LOADING_MORE`) ||
    false

export const getLoadingInitialMessageList =
  (conversationId?: number) =>
  (state: RootState): boolean => {
    if (!conversationId) {
      return false
    }
    const convoFetchStatus = root(state).statusByConversationId[conversationId]
    return convoFetchStatus === `${FETCH_MESSAGES_REQUEST}_INITIAL_LOAD`
  }

export const getLoadingMessagesError =
  (conversationId?: number) =>
  (state: RootState): AxiosError | undefined => {
    return (conversationId && root(state).errorByConversationId[conversationId]) || undefined
  }

export const getLoadingMessagesFailed =
  (conversationId?: number) =>
  (state: RootState): boolean =>
    (conversationId && root(state).statusByConversationId[conversationId]) === FETCH_MESSAGES_FAIL

export const getLoadedInitialList =
  (conversationId: number) =>
  (state: RootState): boolean =>
    !!root(state).fetchedInitialListByConversationId[conversationId]

export const getMessages =
  (conversationId?: number) =>
  (state: RootState): Message[] =>
    (conversationId && root(state).byConversationId[conversationId]) || (emptyArray as Message[])

export const getConversationHasEndUserReply =
  (conversationId?: number) =>
  (state: RootState): boolean => {
    const messages = getMessages(conversationId)(state)
    const firstAgentMessageTimestamp = Math.min(
      ...messages
        .filter((message) => message.authorType === UserIdType.USER && !message.attributes?.isBot)
        .map((message) => message.createdAt)
    )
    return messages.some(
      (message) =>
        [UserIdType.END_USER, UserIdType.LEAD].includes(message.authorType) &&
        message.createdAt >= firstAgentMessageTimestamp
    )
  }

export const getMessageById =
  (conversationId: number, messageId: number) =>
  (state: RootState): Message | undefined => {
    const messages = root(state).byConversationId[conversationId]
    return messages && messages.find((item) => item.id === messageId)
  }

export const fetchMessagesLoading = (state: RootState, conversationId?: number): boolean =>
  (conversationId &&
    root(state).statusByConversationId[conversationId] === FETCH_MESSAGES_REQUEST) ||
  false

export const nextActiveCursor = (state: RootState, conversationId?: number): string | undefined => {
  const pagination = conversationId && root(state).paginationByConversationId[conversationId]
  return pagination ? pagination.next : undefined
}

export const getAttachmentsByMessageId =
  (messageId: number) =>
  (state: RootState): Attachment[] => {
    return root(state).attachmentsByMessageId[messageId] || (emptyArray as Attachment[])
  }

export const getConditionalBranchingLogs = (conversationId?: number) =>
  createSelector(getMessages(conversationId), (messages) => {
    return (messages ?? [])
      .filter((message) => message?.type === MessageType.BOT_NODE_CONDITION_EVALUATION)
      .filter((message) => isDefined(message?.attributes?.evaluatedConditionSets))
      .map((message) => message?.attributes?.evaluatedConditionSets)
  })

const findWithIndex = (messages: Message[], condition: (message: Message) => boolean) => {
  let index = -1
  const foundItem = messages.find((message, idx) => {
    const truthy = condition(message)
    if (truthy) index = idx
    return truthy
  })
  return { index, foundItem }
}

export const getResponseTimerDuration = (conversationId: number) =>
  createSelector(
    getMessages(conversationId),
    hasConversationResponseTimer,
    abilityEnabledFromAnyTeam('responseTimerEnabled'),
    (messages, hasGate, enabled) => {
      if (!enabled || !hasGate) return null

      const now = Date.now()
      const messagesSortByRecent = messages.slice().reverse()
      const { index: endUserIdx } = findWithIndex(messagesSortByRecent, endUserMessageCondition)
      const { index: agentIdx, foundItem: agentMessage } = findWithIndex(
        messagesSortByRecent,
        agentMessageCondition
      )
      const { index: joinEventIdx, foundItem: joinEventMessage } = findWithIndex(
        messagesSortByRecent,
        joinEventCondition
      )

      if (agentMessage) {
        // agent has chatted at least once
        if (agentIdx < endUserIdx) {
          // agent was the most recent message
          return null
        } else {
          // enduser was the most recent message
          const remainingMessagesSortSinceAgent = messagesSortByRecent.slice(0, agentIdx).reverse()
          const firstEndUserMessageSinceAgentReply =
            remainingMessagesSortSinceAgent.find(endUserMessageCondition)
          return firstEndUserMessageSinceAgentReply
            ? now - firstEndUserMessageSinceAgentReply.createdAt
            : null
        }
      } else if (joinEventMessage) {
        // agent hasn't sent any messages yet
        // agent has joined the conversation
        const messagesBeforeJoin = messagesSortByRecent.slice(joinEventIdx)
        let firstEndUserMessageSinceBot
        // search for the first enduser message since the bot replied
        for (const message of messagesBeforeJoin) {
          const isBotMessage = botMessageCondition(message)
          if (!!firstEndUserMessageSinceBot && isBotMessage) break
          const isEndUserMessage = endUserMessageCondition(message)
          if (isEndUserMessage) firstEndUserMessageSinceBot = message
        }
        return firstEndUserMessageSinceBot ? now - firstEndUserMessageSinceBot.createdAt : null
      } else {
        // enduser only chatting with bot
        return null
      }
    }
  )

export const getLanguageDetected = (conversationId: number) =>
  createSelector(getMessages(conversationId), (messages) => {
    const mostRecentMessages = messages.slice().reverse()
    const langDetectedMessage = mostRecentMessages.find(
      (message) => isEndUser(message.authorType) && !!message.attributes?.languageDetectionResult
    )
    return langDetectedMessage?.attributes?.languageDetectionResult?.sourceLanguage
  })

export const getEndUserMessageTranslation = (conversationId: number, messageId: number) =>
  createSelector(getMessages(conversationId), (messages) => {
    const convoTranslationEventMessage = messages.find(
      (m) =>
        m.type === MessageType.CONVERSATION_EVENT &&
        m.conversationEvent?.type === ConversationEventType.CHAT_TRANSLATION_RESPONSE &&
        m.conversationEvent?.chatTranslationResponse?.originalMessageId === messageId
    )

    return convoTranslationEventMessage?.conversationEvent?.chatTranslationResponse?.translatedText
  })
